import { AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Badge, Flex, Switch, Text } from "@chakra-ui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ExclusionGroundsCategoryDto } from "../../../../../autogen/bff-api";
import { ExclusionGroundItem } from "./ExclusionGroundItem";

// t('Mandatory') t('Voluntary') t('Mixed')

interface Props {
  category: ExclusionGroundsCategoryDto;
  activeIds: Set<string>;
  onToggleCriterion: (id: string, active: boolean) => void;
  onToggleCategory: (categoryName: string, active: boolean) => void;
}

type BadgeType = "mandatory" | "voluntary" | "mixed";

const BADGE_CONFIG: Record<BadgeType, { bg: string; color: string; labelKey: string }> = {
  mandatory: { bg: "smQuaternarySoft", color: "smQuaternary", labelKey: "Mandatory" },
  voluntary: { bg: "smSecondarySoft", color: "smSecondary", labelKey: "Voluntary" },
  mixed: { bg: "smPrimarySofter", color: "smPrimary", labelKey: "Mixed" },
};

export const ExclusionGroundsCategoryGroup = ({ category, activeIds, onToggleCriterion, onToggleCategory }: Props) => {
  const { t } = useTranslation();

  const { badgeType, voluntaryCriteria, allVoluntaryActive } = useMemo(() => {
    const hasMandatory = category.criteria.some((c) => c.mandatory);
    const hasVoluntary = category.criteria.some((c) => !c.mandatory);
    let type: BadgeType = "voluntary";
    if (hasMandatory && hasVoluntary) type = "mixed";
    else if (hasMandatory) type = "mandatory";
    const vol = category.criteria.filter((c) => !c.mandatory);
    const allVolActive = vol.length > 0 && vol.every((c) => activeIds.has(c.id));
    return { badgeType: type, voluntaryCriteria: vol, allVoluntaryActive: allVolActive };
  }, [category, activeIds]);

  const badge = BADGE_CONFIG[badgeType];

  return (
    <AccordionItem border="1px solid" borderColor="smBorder" borderRadius="lg" mb="2">
      <AccordionButton py="3" borderRadius="lg">
        <Flex flex="1" align="center" gap="3">
          <Text fontWeight="medium" fontSize="sm">{t(category.code)}</Text>
          <Badge bg={badge.bg} color={badge.color} px="2" py="0.5" borderRadius="md" fontWeight="medium" fontSize="xs" textTransform="none">
            {t(badge.labelKey)}
          </Badge>
          <Text fontSize="xs" color="smMuted">
            {category.criteria.length}
          </Text>
        </Flex>
        {voluntaryCriteria.length > 1 && (
          <Switch
            size="sm"
            isChecked={allVoluntaryActive}
            onChange={(e) => {
              e.stopPropagation();
              onToggleCategory(category.code, !allVoluntaryActive);
            }}
            mr="3"
            onClick={(e) => e.stopPropagation()}
            aria-label={t(category.code) ?? undefined}
          />
        )}
        <AccordionIcon />
      </AccordionButton>
      <AccordionPanel pb="0" px="0">
        {category.criteria.map((criterion) => (
          <ExclusionGroundItem
            key={criterion.id}
            criterion={criterion}
            isActive={criterion.mandatory || activeIds.has(criterion.id)}
            onToggle={onToggleCriterion}
          />
        ))}
      </AccordionPanel>
    </AccordionItem>
  );
};
